/**
 * 文档合成缓存（M2-04）。
 *
 * 维护一张全文档 1:1 的 RGBA8 合成结果与按 256 块粒度的脏集合。
 * 视图每帧只 `update()`：没有脏块就什么都不算——**重算块数等于脏块数**
 * 是 M2-04 的验收标准，也是分块引擎存在的意义在显示侧的体现。
 *
 * 本类不碰 DOM，单元测试直接驱动；把像素放上屏幕是 render-bridge 的事。
 */
import type { Document } from '../document/types';
import { TILE } from '../engine/tile/key';
import type { TileStore } from '../engine/tile/store';
import { compositeRegion } from './composite';

export class CompositeCache {
  readonly width: number;
  readonly height: number;
  /** 全文档合成结果，RGBA8 直通 alpha。 */
  readonly pixels: Uint8Array;

  readonly #tilesX: number;
  readonly #dirty = new Set<number>();
  readonly #scratch = new Uint8Array(TILE * TILE * 4);

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.pixels = new Uint8Array(width * height * 4);
    this.#tilesX = Math.ceil(width / TILE);
    this.markAllDirty();
  }

  get dirtyCount(): number {
    return this.#dirty.size;
  }

  markAllDirty(): void {
    const tilesY = Math.ceil(this.height / TILE);
    for (let ty = 0; ty < tilesY; ty++) {
      for (let tx = 0; tx < this.#tilesX; tx++) {
        this.#dirty.add(ty * this.#tilesX + tx);
      }
    }
  }

  /** 文档空间块坐标标脏。越界的坐标忽略——脏区宁多勿漏，但不越过文档边界。 */
  markTileDirty(tx: number, ty: number): void {
    if (tx < 0 || ty < 0 || tx >= this.#tilesX || ty * TILE >= this.height) return;
    this.#dirty.add(ty * this.#tilesX + tx);
  }

  /**
   * 重算所有脏块，返回本次重算的块数。
   * 干净时开销为零——这个方法在每帧渲染里被调用，必须能安心空转。
   */
  update(document: Document, tiles: TileStore, skipLayerId?: string): number {
    if (this.#dirty.size === 0) return 0;
    const updated = this.#dirty.size;

    for (const index of this.#dirty) {
      const tx = index % this.#tilesX;
      const ty = Math.floor(index / this.#tilesX);
      const rect = {
        x: tx * TILE,
        y: ty * TILE,
        width: Math.min(TILE, this.width - tx * TILE),
        height: Math.min(TILE, this.height - ty * TILE),
      };
      compositeRegion(document, tiles, rect, this.#scratch, skipLayerId);

      // 把块结果拷回整图缓存
      for (let y = 0; y < rect.height; y++) {
        const src = y * rect.width * 4;
        const dst = ((rect.y + y) * this.width + rect.x) * 4;
        this.pixels.set(this.#scratch.subarray(src, src + rect.width * 4), dst);
      }
    }
    this.#dirty.clear();
    return updated;
  }
}
